import type { Distance } from "@pdfking/math"
import { AddMargins, RemoveMargins, Subtract, Total } from "@pdfking/math"
import { ZERO_DISTANCE } from "@pdfking/math/constants"
import type { PageShape } from "@pdfking/renderer-api"
import { PlotFunction } from "@pdfking/renderer-api"

import { PALETTE } from "#constants"
import type { PageContext } from "#internal"

import { DrawFooter } from "../calendar/DrawFooter"
import { LogoFooterHeight } from "../layout/LogoFooterHeight"
import { PlotNotebookBackground } from "../layout/PlotNotebookBackground"
import { NotebookBackground } from "../types/NotebookBackground"

// A notepad's `page` of `size`, its corner at the origin, as the shop's
// templates draw it within `security`: with a `footer`, the calendars' at the
// bottom of the frame (see DrawFooter), and above it, else down to the frame's
// foot, its `background` `pitch` apart (see PlotNotebookBackground); the paper
// runs on into `bleed`. Lines `edge_to_edge` run from edge to edge into the
// bleed, as the notebooks' templates rule them, the security only placing them
// down the page
export const DrawNotepadPage = ({
  background,
  edge_to_edge = false,
  footer,
  page: { bleed, labels, security, size },
  pitch,
}: {
  background: NotebookBackground
  edge_to_edge?: boolean
  footer: boolean
  page: PageContext
  pitch: Distance
}): PageShape[] => {
  const page = { ...size, x: ZERO_DISTANCE, y: ZERO_DISTANCE }
  const area = AddMargins(page, bleed)
  const outer = RemoveMargins(page, security)
  const foot = footer ? LogoFooterHeight(size) : ZERO_DISTANCE
  const frame = { ...outer, height: Subtract(outer.height, foot), y: Total(outer.y, foot) }
  const box =
    edge_to_edge && background === NotebookBackground.LINES
      ? { ...frame, width: area.width, x: area.x }
      : frame
  return [
    { enum: PlotFunction.PLOT_RECT, fill_color: PALETTE.paper, position: area },
    ...PlotNotebookBackground({ background, box, pitch }),
    ...(footer
      ? DrawFooter({ box: { ...outer, height: LogoFooterHeight(size, false) }, labels })
      : []),
  ]
}
